import React from 'react';
import {
  FormRows,
  FormTemplate,
  BulletList, SimpleBlock, SubmitButton,
  Text,
  useFormContextRequired
} from 'ui-builder';
import {
  Holiday,
  UpdateBusinessDaysConfigurationRequest,
  useBusinessDaysConfigurationService, useDateHelper,
  useHolidaysService
} from 'nekst-api';
import { useDataLoaderNew } from 'data-loader';
import { CountryDropdown } from 'features/nekst-widgets';

import styles from './HolidaysInfo.module.scss';

function HolidaysInfo() {
  const holidaysService = useHolidaysService();

  const formContext = useFormContextRequired<UpdateBusinessDaysConfigurationRequest>();

  const { data: holidays } = useDataLoaderNew(
    async () => {
      const countryCode = formContext.data?.holidaysCountry;
      if (!countryCode) {
        return [];
      } else {

        const currentYear = new Date().getFullYear();

        const nextYear = new Date().getFullYear() + 1;

        return await (Promise.all(
          [currentYear, nextYear].map((year) => holidaysService.getHolidays(countryCode, year))
        )
          .then((values) => values.flat()));
      }
    },
    {
      dependencies: [formContext.data?.holidaysCountry],
    },
  );

  const dateHelper = useDateHelper();

  const renderHoliday = (holiday: Holiday) => {
    return `${dateHelper.toString(holiday.date)} - ${holiday.name}`;
  };

  return (
    <SimpleBlock className={styles.items}>
      <BulletList
        items={holidays?.map(renderHoliday) || []}
      />
    </SimpleBlock>
  );
}

export default function BusinessDaysConfigurationForm() {
  const {
    getConfiguration,
    updateConfiguration
  } = useBusinessDaysConfigurationService();

  return (
    <FormTemplate<UpdateBusinessDaysConfigurationRequest>
      getDataFunc={async () => {
        const returnConfiguration = await getConfiguration();

        if (returnConfiguration.excludeHolidays) {
          return {
            holidaysCountry: returnConfiguration.holidaysCountry,
            excludeHolidays: true,
          }
        } else {
          return {
            excludeHolidays: false,
          };
        }

      }}
      submitFormFunc={async (data) => {
        if (data.holidaysCountry) {
          await updateConfiguration(undefined, {
            ...data,
            excludeHolidays: true,
          });
        } else {
          await updateConfiguration(undefined, {
            excludeHolidays: false,
          });
        }

        return data;
      }}
      validationSchema={{
        holidaysCountry: {
          type: 'string',
          constraints: {
            maxLength: 50,
          },
        },
      }}
      buttons={<SubmitButton />}
    >
      <FormRows>
        <Text>
          For tasks based upon business days, the following holidays should also be observed. Should a task fall on one of these business days, the due date will shift forward one additional business day .
        </Text>
        <CountryDropdown
          source="holidaysCountry"
          label="Country"
          placeholder="None"
        />
        <HolidaysInfo />
      </FormRows>

    </FormTemplate>
  );
}
